import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SendOption } from "@models/annual-statements/annual-statement-enums";
import { Document } from "@models/documents/document";
import { DocumentSigning } from "@models/documents/document-signing";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { LodgementDeadline } from "@models/documents/lodgement-deadline";
import { NotificationComponent } from "@shared/components/commons/notification/notification.component";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentsService } from "@shared/services/documents.service";
import { ToastrService } from "ngx-toastr";
import { catchError, EMPTY, filter, Observable, of, Subject, switchMap } from "rxjs";

@Component({
  selector: 'app-lodge-documents',
  standalone: true,
  imports: [NotificationComponent],
  providers: [],
  templateUrl: './lodge-documents.component.html',
  styleUrl: './lodge-documents.component.scss'
})
export class LodgeDocumentsComponent implements OnInit {
  private documentsService = inject(DocumentsService);
  private documentsApiService = inject(DocumentsApiService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  @Input() documentStatus: DocumentStatusEnum | undefined;
  @Input() sendBy: SendOption | undefined | null;
  @Input() documentId: string | undefined;
  @Input() lodgement: LodgementDeadline | undefined;
  @Input() documentSigning: DocumentSigning | null | undefined;
  @Input() confirmAction$: Subject<boolean> | undefined;

  @Output() nextStep = new EventEmitter<boolean>();
  @Output() reloadDocument = new EventEmitter<boolean>();

  readonly lateFeeText = '"Late Fee" is a term referring to an additional charge imposed when a payment is not made by the due date.' +
    ' It\'s a penalty enforced by various service providers, such as banks, utility companies, or rental agencies,' +
    ' to incentivize timely payments and compensate for the inconvenience caused by delays.' +
    ' Late fees can vary in amount and are typically outlined in the terms and conditions of contracts or agreements.';

  readonly prepareLodgementInfo = 'In the "Prepare Lodgement" step,' +
    ' the verification of signed document status is conducted to ensure adherence to regulatory requirements.' +
    ' This pivotal phase involves thorough review to confirm the completeness of signatures,' +
    ' thereby certifying the readiness of documents for submission or subsequent processing.';

  amountFee = 0;

  constructor() {
    this.toastr.toastrConfig.positionClass = 'toast-top-right';
  }

  ngOnInit(): void {
    this.amountFee = this.lodgement?.lateFee ? this.lodgement.lateFee : 0;
    this.disableHeaderBtn(false);
    this.listenHeaderButton();
  }

  private listenHeaderButton(): void {
    this.confirmAction$?.pipe(
      filter(confirm => confirm),
      switchMap(() => {
        if(this.sendBy === SendOption.Sign || this.sendBy === SendOption.Paper) {
          return this.sendToAsic();
        } else {
          return EMPTY;
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((res) => {
      if(res !== 'ERR') {
        this.toastr.success('Document sent for lodgement', 'Success');
        this.nextStep.emit(true);
        this.reloadDocument.emit(true);
        this.confirmAction$?.next(false);
      }
    });
  }

  private sendToAsic(): Observable<string | string[] | Document> {
    const documentId = this.documentId ?? '';
    return this.documentsApiService.lodgeDocument([documentId]).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        this.toastr.error("Error lodging documents", "Error");
        return of('ERR');
      }),
    );
  }

  disableHeaderBtn(value: boolean): void {
    this.documentsService.disabledHeaderBtn.set(value);
  }
}
